import { createSelector, createSlice } from "@reduxjs/toolkit";

import {
    getHomeLeagues,
    getMasterTournaments,
    getTournaments,
    updateHomeLeagues,
} from "./tournamentManagementActions";

import { authActions } from "../auth/authSlice";

import { RootState } from "../../store";
import { IMasterTournament, ITournament } from "../../types/api";
import { IHomeLeague } from "../../types/forms";
import { IStateDefaults } from "../../types/shared";

interface TournamentManagementState extends IStateDefaults {
    masterTournaments: IMasterTournament[];
    tournaments: ITournament[];
    selectedTournament?: ITournament["id"] | IMasterTournament["id"];
    homeLeagues: IHomeLeague[];
}

const initialState: TournamentManagementState = {
    masterTournaments: [],
    tournaments: [],
    homeLeagues: [],
    selectedTournament: undefined,
    loading: false,
    success: false,
};

const tournamentManagementSlice = createSlice({
    name: "tournamentmanagement",
    initialState,
    reducers: {
        setselectedTournamentId: (state, action) => {
            state.selectedTournament = action.payload;
        },
        setMasterTournaments: (state, action) => {
            state.masterTournaments = action.payload;
        },
        setTournaments: (state, action) => {
            state.tournaments = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.tournaments = [];
                state.selectedTournament = undefined;
            })
            .addCase(getTournaments.pending, state => {
                state.loading = true;
            })
            .addCase(getTournaments.fulfilled, (state, { payload }) => {
                state.tournaments = payload.tournaments;
                state.loading = false;
            })
            .addCase(getTournaments.rejected, state => {
                state.loading = false;
            })
            .addCase(getMasterTournaments.pending, state => {
                state.loading = true;
            })
            .addCase(getMasterTournaments.fulfilled, (state, { payload }) => {
                state.masterTournaments = payload.tournaments;
                state.loading = false;
            })
            .addCase(getMasterTournaments.rejected, state => {
                state.loading = false;
            })
            .addCase(getHomeLeagues.pending, state => {
                state.loading = true;
            })
            .addCase(getHomeLeagues.fulfilled, (state, { payload }) => {
                state.homeLeagues = payload.homeLeagues;
                state.loading = false;
            })
            .addCase(getHomeLeagues.rejected, state => {
                state.loading = false;
            })
            .addCase(updateHomeLeagues.pending, state => {
                state.loading = true;
            })
            .addCase(updateHomeLeagues.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.homeLeagues = payload ? payload : [];
            })
            .addCase(updateHomeLeagues.rejected, state => {
                state.loading = false;
            });
    },
});

export const tournamentManagementActions = {
    ...tournamentManagementSlice.actions,
    getTournaments: getTournaments,
    getMasterTournaments: getMasterTournaments,
    getHomeLeagues: getHomeLeagues,
    updateHomeLeagues: updateHomeLeagues,
};

export default tournamentManagementSlice.reducer;

const selectSelectedTournamentWrapper = createSelector(
    [
        (state: RootState) => state.tournamentmanagement,
        (state: RootState, tournamentType?: "child" | "master" | "either") =>
            tournamentType,
    ],
    (state, tournamentType): ITournament | IMasterTournament | undefined => {
        const selectedTournament = state.selectedTournament;
        if (!selectedTournament) return undefined;

        if (tournamentType === "child") {
            return state.tournaments.find(
                tournament => tournament.id === selectedTournament,
            ) as ITournament | undefined;
        }
        if (tournamentType === "master") {
            return state.masterTournaments.find(
                tournament => tournament.id === selectedTournament,
            ) as IMasterTournament | undefined;
        }
        return (
            state.tournaments.find(
                tournament => tournament.id === selectedTournament,
            ) ||
            state.masterTournaments.find(
                tournament => tournament.id === selectedTournament,
            )
        );
    },
);

export function selectSelectedTournament(
    state: RootState,
    tournamentType: "child",
): ITournament | undefined;
export function selectSelectedTournament(
    state: RootState,
    tournamentType: "master",
): IMasterTournament | undefined;
export function selectSelectedTournament(
    state: RootState,
    tournamentType?: "either",
): ITournament | IMasterTournament | undefined;
export function selectSelectedTournament(
    state: RootState,
    tournamentType?: "child" | "master" | "either",
): ITournament | IMasterTournament | undefined {
    return selectSelectedTournamentWrapper(state, tournamentType);
}
